import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { useFirebaseEmulators, isProductionEnv } from '../../config/env';
import { hasFirebaseConfig } from '../../config/firebaseConfig';

export type AppCheckBootstrap = {
  enabled: boolean;
  mode?: string;
};

let bootstrapResult: AppCheckBootstrap = { enabled: false };
let bootstrapPromise: Promise<AppCheckBootstrap> | null = null;

/**
 * @param {import('firebase/app').FirebaseApp} app
 */
export async function initAppCheck(app: FirebaseApp) {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    if (useFirebaseEmulators()) {
      (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean })
        .FIREBASE_APPCHECK_DEBUG_TOKEN =
        import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim() || true;
      bootstrapResult = { enabled: false, mode: 'emulator-skipped' };
      return bootstrapResult;
    }

    const siteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY?.trim();
    if (!siteKey) {
      const message = '[AppCheck] VITE_FIREBASE_APPCHECK_SITE_KEY missing in production';
      if (isProductionEnv()) {
        console.error(message);
      } else {
        console.warn(message);
      }
      bootstrapResult = { enabled: false };
      return bootstrapResult;
    }

    try {
      // Console App Check for this project is registered with reCAPTCHA Enterprise
      // (exchangeRecaptchaV3Token returns "App not registered").
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      bootstrapResult = { enabled: true, mode: 'recaptcha-enterprise' };
      return bootstrapResult;
    } catch (err) {
      console.warn('[AppCheck] init failed:', err);
      bootstrapResult = { enabled: false };
      return bootstrapResult;
    }
  })();

  return bootstrapPromise;
}

/**
 * Resolves after App Check bootstrap completes.
 * If platform bootstrap has not started yet, starts App Check lazily so callables
 * never race ahead of token minting when Functions enforce App Check.
 */
export async function ensureAppCheckReady(): Promise<AppCheckBootstrap> {
  if (bootstrapPromise) return bootstrapPromise;
  if (!hasFirebaseConfig() || useFirebaseEmulators()) {
    return bootstrapResult;
  }
  const app = (await import('../../config/firebase.js')).default;
  return initAppCheck(app);
}

export function getAppCheckStatus(): AppCheckBootstrap {
  return bootstrapResult;
}
